import { useImmer } from "use-immer";
import { useEffect, useRef, useState } from "react";
import { faCircleQuestion } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Note from "../Note";
import WeightConfig from "./WeightConfig";
import TresholdConfig from "./TresholdConfig";
import CustomerCardDeckEwPayRuleConfig from "../configPageEwPay/CustomerCardDeckEwPayRuleConfig";
import CustomerLimitEwPayRuleConfig from "../configPageEwPay/CustomerLimitEwPayRuleConfig";
import CustomerSuddenHotspotVisitEwPayRuleConfig from "../configPageEwPay/CustomerSuddenHotspotVisitEwPayRuleConfig";

const Component = ({ config, generalConfig, onConfigChange, expandAll, onExpandAllChange }) => {
  const [data, updateData] = useImmer(config);
  const [generalConfigData, updateGeneralConfigData] = useImmer(generalConfig);
  const containerRef = useRef(null);
  const skipEffectRef = useRef(false);

  const [weightsInfoOpen, setWeightsInfoOpen] = useState(false);
  const [thresholdInfoOpen, setThresholdInfoOpen] = useState(false);
  const [cardDeckInfoOpen, setCardDeckInfoOpen] = useState(false);
  const [limitInfoOpen, setLimitInfoOpen] = useState(false);
  const [hotspotInfoOpen, setHotspotInfoOpen] = useState(false);

  const handleInfoIconClick = (detailsId, isOpen, setOpen) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const detailsElement = document.getElementById(detailsId);
    if (!detailsElement.open) {
      detailsElement.open = true;
      setOpen(true);
    } else {
      setOpen(!isOpen);
    }
  };

  useEffect(() => {
    onConfigChange(data);
  }, [data]);

  useEffect(() => {
    updateData(config);
    updateGeneralConfigData(generalConfig);
  }, [config]);

  useEffect(() => {
    if (skipEffectRef.current) {
      skipEffectRef.current = false;
      return;
    }
    if (containerRef.current) {
      const details = containerRef.current.querySelectorAll('details.config-section');
      details.forEach(detail => {
        detail.open = expandAll;
      });
      setTimeout(() => {
        const openStates = {};
        details.forEach((detail) => {
          const sectionId = detail.getAttribute('data-section-id');
          if (sectionId) {
            openStates[sectionId] = detail.open;
          }
        });
        sessionStorage.setItem('ewPayDetailsState', JSON.stringify(openStates));
      }, 150);
    }
  }, [expandAll]);

  // Load state from sessionStorage on mount only
  useEffect(() => {
    const timer = setTimeout(() => {
      if (containerRef.current) {
        const savedState = sessionStorage.getItem('ewPayDetailsState');
        if (savedState) {
          try {
            const openStates = JSON.parse(savedState);
            const details = containerRef.current.querySelectorAll('details.config-section');
            details.forEach((detail) => {
              const sectionId = detail.getAttribute('data-section-id');
              if (sectionId && openStates[sectionId] !== undefined) {
                detail.open = openStates[sectionId];
              }
            });
          } catch (e) {
            console.error('Failed to restore details state:', e);
          }
        }
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;

    const handleToggle = () => {
      const details = containerRef.current.querySelectorAll('details.config-section');

      // Save current state to sessionStorage
      const openStates = {};
      details.forEach((detail) => {
        const sectionId = detail.getAttribute('data-section-id');
        if (sectionId) {
          openStates[sectionId] = detail.open;
        }
      });
      sessionStorage.setItem('ewPayDetailsState', JSON.stringify(openStates));

      // Check if all are open and update expandAll if needed
      if (expandAll) {
        const allOpen = Array.from(details).every(detail => detail.open);
        if (!allOpen && onExpandAllChange) {
          skipEffectRef.current = true;
          onExpandAllChange(false);
        }
      }
    };

    const details = containerRef.current.querySelectorAll('details.config-section');
    details.forEach(detail => {
      detail.addEventListener('toggle', handleToggle);
    });

    return () => {
      details.forEach(detail => {
        detail.removeEventListener('toggle', handleToggle);
      });
    };
  }, [expandAll, onExpandAllChange]);

  return (
    <div ref={containerRef}>
      <details className="config-section" data-section-id="ewPayWeights" id="ewPayWeights-details">
        <summary>
          <h3>
            Ew Pay Weights{" "}
            <span className="section-header-icons">
              <Note
                noteTargetPath="rulesConfig.ewPayWeight"
                noteTargetId={data.id}
              />
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="info-icon"
                onClick={handleInfoIconClick('ewPayWeights-details', weightsInfoOpen, setWeightsInfoOpen)}
                title={weightsInfoOpen ? "Hide info" : "Show info"}
              />
            </span>
          </h3>
        </summary>

        {weightsInfoOpen && (
          <div
            className="info-box"
          >
            <p>
              <strong>Defines how risk scores are assigned</strong>, either manually or automatically via "Auto Update."
              Users should generally avoid modifying automatically updated scores.
            </p>
          </div>
        )}

        <WeightConfig
          configId={data.id}
          weightConfig={data.riskEvaluation.ewPayWeightConfig}
          weightManualConfig={data.riskEvaluation.ewPayWeightManualConfig || {}}
          ruleSuppressGeneralConfig={generalConfigData.generalConfig.ruleSuppressConfig}
          onChange={({ weightConfig, weightManualConfig }) =>
            updateData((d) => {
              d.riskEvaluation.ewPayWeightConfig = weightConfig;
              d.riskEvaluation.ewPayWeightManualConfig = weightManualConfig || {};
            })
          }
        />
      </details>

      <details className="config-section" data-section-id="ewPayTreshold" id="ewPayTreshold-details">
        <summary>
          <h3>
            Ew Pay Treshold{" "}
            <span className="section-header-icons">
              <Note
                noteTargetPath="rulesConfig.ewPayTreshold"
                noteTargetId={data.id}
              />
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="info-icon"
                onClick={handleInfoIconClick('ewPayTreshold-details', thresholdInfoOpen, setThresholdInfoOpen)}
                title={thresholdInfoOpen ? "Hide info" : "Show info"}
              />
            </span>
          </h3>
        </summary>

        {thresholdInfoOpen && (
          <div
            className="info-box"
          >
            <p>
              <strong>Defines what score has to be reached</strong> for Pablo to take action.
              Users should generally avoid modifying automatically updated thresholds.
            </p>
          </div>
        )}

        <TresholdConfig
          configId={data.id}
          tresholdConfig={data.riskEvaluation.ewPayTresholdConfig}
          manualConfig={data.riskEvaluation.ewPayTresholdManualConfig || {}}
          onChange={({ tresholdConfig, manualConfig }) =>
            updateData((d) => {
              d.riskEvaluation.ewPayTresholdConfig = tresholdConfig;
              d.riskEvaluation.ewPayTresholdManualConfig = manualConfig || {};
            })
          }
        />
      </details>

      <details className="config-section" data-section-id="customerCardDeckEwPayRule" id="customerCardDeckEwPayRule-details">
        <summary>
          <h3>
            Customer card deck Ew Pay rule{" "}
            <span className="section-header-icons">
              <Note
                noteTargetPath="rulesConfig.customerCardDeckEwPayRule"
                noteTargetId={data.id}
              />
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="info-icon"
                onClick={handleInfoIconClick('customerCardDeckEwPayRule-details', cardDeckInfoOpen, setCardDeckInfoOpen)}
                title={cardDeckInfoOpen ? "Hide info" : "Show info"}
              />
            </span>
          </h3>
        </summary>

        {cardDeckInfoOpen && (
          <div
            className="info-box"
          >
            <p>
              <strong>Increases the risk score</strong> when a high number of unique "EW Pay" cards are used at a single
              merchant within a short window. To prevent false positives at busy locations, the rule is
              automatically disabled if the merchant shows a consistently high volume of legitimate Ew Pay
              traffic over a longer historical period.
            </p>
          </div>
        )}

        <CustomerCardDeckEwPayRuleConfig
          config={data.riskEvaluation.customerCardDeckEwPayRuleConfig}
          onChange={(e) =>
            updateData((d) => {
              d.riskEvaluation.customerCardDeckEwPayRuleConfig = e;
            })
          }
        />
      </details>

      <details className="config-section" data-section-id="customerLimitEwPayRule" id="customerLimitEwPayRule-details">
        <summary>
          <h3>
            Customer limit Ew Pay rule{" "}
            <span className="section-header-icons">
              <Note
                noteTargetPath="rulesConfig.customerLimitEwPayRule"
                noteTargetId={data.id}
              />
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="info-icon"
                onClick={handleInfoIconClick('customerLimitEwPayRule-details', limitInfoOpen, setLimitInfoOpen)}
                title={limitInfoOpen ? "Hide info" : "Show info"}
              />
            </span>
          </h3>
        </summary>

        {limitInfoOpen && (
          <div
            className="info-box"
          >
            <p>
              <strong>Calculates dynamic spending limits</strong> per Company ID for EW Pay transactions based on customer activity.
              For active customers, the limit is derived from their peak 24-hour historical spending plus a safety buffer,
              while new or inactive customers are assigned fixed limits. The rule tracks the "Remaining Limit" by deducting
              recent usage from this total to detect excessive spending.
            </p>
          </div>
        )}

        <CustomerLimitEwPayRuleConfig
          config={data.riskEvaluation.customerLimitEwPayRuleConfig}
          onChange={(e) =>
            updateData((d) => {
              d.riskEvaluation.customerLimitEwPayRuleConfig = e;
            })
          }
        />
      </details>

      <details className="config-section" data-section-id="customerSuddenHotspotVisitRule" id="customerSuddenHotspotVisitRule-details">
        <summary>
          <h3>
            Customer sudden hotspot visit rule{" "}
            <span className="section-header-icons">
              <Note
                noteTargetPath="rulesConfig.ewPayWeight"
                noteTargetId={data.id}
              />
              <FontAwesomeIcon
                icon={faCircleQuestion}
                className="info-icon"
                onClick={handleInfoIconClick('customerSuddenHotspotVisitRule-details', hotspotInfoOpen, setHotspotInfoOpen)}
                title={hotspotInfoOpen ? "Hide info" : "Show info"}
              />
            </span>
          </h3>
        </summary>

        {hotspotInfoOpen && (
          <div
            className="info-box"
          >
            <p>
              <strong>Increases the risk score</strong> when an "Inactive" or "Partially Active" customer uses EW Pay at a hotspot.
              The rule classifies customers based on their transaction history over the last 90 days and flags sudden
              activity at high-risk locations by previously dormant accounts.
            </p>
          </div>
        )}

        <CustomerSuddenHotspotVisitEwPayRuleConfig
          config={data.riskEvaluation.customerSuddenHotspotVisitEwPayRuleConfig}
          onChange={(e) =>
            updateData((d) => {
              d.riskEvaluation.customerSuddenHotspotVisitEwPayRuleConfig = e;
            })
          }
        />
      </details>
    </div>
  );
};

export default Component;
